/**
 * A bidder's view preferences, stored on their account rather than the device.
 *
 * The design system carries three ART DIRECTIONS for lot detail — Refined,
 * Classic and Editorial — and ten Live Room layouts. Jim, 2026-08-05: "The
 * intention was to give the users options for their view." Account-level
 * storage is what makes that real: a bidder who picks Editorial on their phone
 * should not find Refined waiting for them on the website.
 *
 * EVERY READ TOLERATES A VALUE IT DOES NOT RECOGNISE. The columns deliberately
 * carry no CHECK constraint so the design's set can grow without a schema
 * change, which means an older client WILL eventually meet an id from a newer
 * release. Falling back is the contract, not a defensive nicety — throwing
 * would break the lot page for anyone who had used a newer app.
 */

import type { AuctisyncSupabase } from './client.js';
import { normalizeLayoutId, type LiveRoomLayoutId } from './live-room-layout.js';

/** The art directions, in the order a picker should offer them. */
export const LOT_VIEW_PRESETS = ['modern', 'classic', 'editorial'] as const;
export type LotViewPreset = (typeof LOT_VIEW_PRESETS)[number];

/**
 * "Refined" in the design handoff is `modern` in the preset CSS — the same
 * direction under two names. The token package uses the CSS name, so the
 * default is spelled that way here to avoid a translation layer nobody would
 * remember existed.
 */
export const DEFAULT_LOT_VIEW_PRESET: LotViewPreset = 'modern';

export function isLotViewPreset(value: unknown): value is LotViewPreset {
  return typeof value === 'string' && (LOT_VIEW_PRESETS as readonly string[]).includes(value);
}

/** Coerce anything — null, legacy, a newer release's id — to a usable preset. */
export function resolveLotViewPreset(value: unknown): LotViewPreset {
  return isLotViewPreset(value) ? value : DEFAULT_LOT_VIEW_PRESET;
}

/**
 * Read the signed-in bidder's preset.
 *
 * Returns the default rather than throwing on any failure. A preference is not
 * worth failing a lot page over: someone trying to look at an item they might
 * bid on should see it, in whatever direction we can manage.
 */
export async function fetchLotViewPreset(
  supabase: AuctisyncSupabase,
  userId: string,
): Promise<LotViewPreset> {
  const { data, error } = await supabase
    .from('profiles')
    .select('lot_view_preset')
    .eq('id', userId)
    .maybeSingle();

  if (error || !data) return DEFAULT_LOT_VIEW_PRESET;
  return resolveLotViewPreset((data as { lot_view_preset?: unknown }).lot_view_preset);
}

/**
 * Persist the bidder's choice.
 *
 * Reports whether it stuck. A caller should still show the new direction
 * immediately either way — the preference has already been applied locally, and
 * a failed write means "this will not follow you to your other devices", not
 * "your choice was refused".
 */
export async function saveLotViewPreset(
  supabase: AuctisyncSupabase,
  userId: string,
  preset: LotViewPreset,
): Promise<{ saved: boolean }> {
  const { error } = await supabase
    .from('profiles')
    .update({ lot_view_preset: preset })
    .eq('id', userId);
  return { saved: error === null };
}

/**
 * The bidder's stored Live Room layout, or null when they have never chosen.
 *
 * Null rather than the default on purpose: the caller has a device-local value
 * to fall back to before reaching for the default, and collapsing "no account
 * preference" into "Closing Reel" here would silently outrank it.
 */
export async function fetchLiveRoomLayout(
  supabase: AuctisyncSupabase,
  userId: string,
): Promise<LiveRoomLayoutId | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('live_room_layout')
    .eq('id', userId)
    .maybeSingle();

  if (error || !data) return null;
  return normalizeLayoutId((data as { live_room_layout?: unknown }).live_room_layout);
}

/**
 * Persist the choice to the account.
 *
 * Writes the SAME column the website writes, which is the whole point: a bidder
 * who picks Gallery Wall on their phone should find Gallery Wall on the site.
 */
export async function saveLiveRoomLayout(
  supabase: AuctisyncSupabase,
  userId: string,
  /**
   * 🔴 WIDENED TO ACCEPT A SURFACE-LOCAL ID (2026-09-15).
   *
   * The website has an eleventh layout, `catalogue`, that the phone does not
   * have and must not get. It is declared web-side and is deliberately absent
   * from `LIVE_ROOM_LAYOUTS`, so it is not a `LiveRoomLayoutId` and could not be
   * written through this function as it was typed.
   *
   * Widening the WRITE is safe and is already the design: the column is plain
   * nullable `text` with no CHECK constraint, and every READ goes through a
   * normalizer that validates against the reading surface's own list and
   * returns null for anything else — the module's own header states it, *"Unknown
   * / legacy values are ignored at each level"*. A phone reading `catalogue`
   * falls back to its default; it does not write on read, so the bidder's
   * website choice survives.
   *
   * `(string & {})` rather than plain `string` so editors still autocomplete the
   * known ids instead of offering nothing.
   */
  layout: LiveRoomLayoutId | (string & {}),
): Promise<{ saved: boolean }> {
  const { error } = await supabase
    .from('profiles')
    .update({ live_room_layout: layout })
    .eq('id', userId);
  return { saved: error === null };
}
